<script context="module" lang="ts">
	import Section from '$lib/components/layout/Section.svelte';
	import Accordion from '$lib/components/util/Accordion.svelte';
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';
	import TextWithIcon from '$lib/components/util/TextWithIcon.svelte';
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';
	import { PUBLIC_DIRECTUS_BACKEND } from '$env/static/public';
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import scrollAction from '$lib/actions/useScrollAction';

	export const config: SectionConfig = {
		collection: 'section_cafes_stores',
		availableFor: ['pages'],
		query: gql`
        internal_name
        translations {
            languages_code {
                code
            }
            title
        }
		countries {
			cafe_store_countries_id {
				shops {
					link {
						...Link
					}
					phone
					email
					translations {
						languages_code {
							code
						}
						title
						address
					}
				}
				key
				translations {
					languages_code {
						code
					}
					name
				}
			}
		}
		mobile_icon {
			...Media
		}
    `
	};
	type Props = {
		title: string;
		countries: any;
		link: any;
		mobile_icon: any;
	};
</script>

<script lang="ts">
	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)
	let accordionRef: any;

	let selectedCountry: string = '';

	const handleButtonClick = (countryName: string) => {
		selectedCountry = selectedCountry === countryName ? '' : countryName;
		if (accordionRef) {
			accordionRef.openItemById(countryName);
		}
	};

	const scrollRight = () => {
		const countryWrapper = document.querySelector('.country-wrapper');
		if (countryWrapper) {
			countryWrapper.scrollBy({ left: 200, behavior: 'smooth' });
		}
	};
</script>

<Section gapType="padding">
	<div class="wrapper">
		<div class="title">
			{data.title}
		</div>
		<div class="country-wrapper" use:scrollAction>
			{#each data.countries as { cafe_store_countries_id: item }}
				<div
					class="country-button"
					class:active={selectedCountry === item.name}
					on:click={() => handleButtonClick(item.name)}
				>
					{item.name}
				</div>
			{/each}

			<div class="scroll-arrow" on:click={scrollRight}><GlobalMedia key="icon_caret_grey" /></div>
		</div>

		<div class="accordion-wrapper">
			<Accordion
				bind:this={accordionRef}
				name="cafe-store-accordion"
				items={data.countries.map((country) => ({
					id: country.cafe_store_countries_id.name,
					title: country.cafe_store_countries_id.name,
					text: country.cafe_store_countries_id.shops
						.map((item) => {
							return `
					        <div class="accordion-content-wrapper">
								<div class="accordion-country-title">${item.title}</div>
								<div class="accordion-content">
									<div classs="country-address"> ${item.address}</div>
									<div class="accordion-phone"><span>P:</span> <a class="accordion-phone" href="tel:${item.phone}">${item.phone}</a></div>
									<div class="accordion-email"><span>M:</span> <a class="accordion-email" href="mailto:${item.email}">${item.email}</a></div>
								</div>
								<div><a class="accordion-link" href=${item.link.link} target="_blank">${item.link.display_text}<span><img src=${PUBLIC_DIRECTUS_BACKEND}assets/${item.link.icon.id} alt=""/></span></a></div>
								<div><a class="accordion-link-mobile" href=${item.link.link} target="_blank"><img src=${PUBLIC_DIRECTUS_BACKEND}assets/${data.mobile_icon.id} alt=""/></span></a></div>
							</div>
							`;
						})
						.join('<br><br>')
				}))}
			/>
		</div>
	</div>
</Section>

<style lang="scss">
	.wrapper {
		padding: 0 v(content-padding-5xl);
		@media (max-width: 1400px) {
			padding: 0;
		}
	}
	.title {
		@include font(h2);
		max-width: 20ch;
		margin-bottom: v(content-gap);
	}

	.country-wrapper {
		display: flex;
		gap: v(content-gap-sm);
		margin-bottom: v(content-gap);
		// position: relative;
		overflow-x: auto;
		scroll-behavior: smooth;
		@include media(tablet) {
			gap: v(content-gap-sm);
		}
		::-webkit-scrollbar {
			display: none;
		}
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none; /* Firefox */

		@media (max-width: 650px) {
			padding-inline: v(page-padding);
			margin-inline: calc(-1 * v(page-padding));

			&:global(.reached-horizontal-end) {
				.scroll-arrow {
					opacity: 0;
				}
			}
		}
	}
	.scroll-arrow {
		display: none;
		@media (max-width: 650px) {
			@include transition;
			display: flex;
			background: transparent;
			font-size: 1.5rem;
			position: absolute;
			inset-inline-end: 0;
			z-index: 1;
			width: 40px;
			height: 1.5em;
			display: flex;
			justify-content: flex-end;
			align-items: center;

			:global(svg) {
				height: 16px;
				width: 16px;
				margin-inline-end: 8px;
			}
			background: linear-gradient(
				90deg,
				rgba(255, 255, 255, 0) 0%,
				color(background) 25%,
				color(background) 100%
			);
		}
	}
	.accordion-wrapper {
		:global(.item) {
			display: block;
			width: 100%;
		}
		:global(.item.open .text) {
			@media (min-width: 1800px) {
				justify-content: flex-start;
			}
			@media (max-width: 600px) {
				display: flex;
				justify-content: center;
			}
		}
		:global(.title) {
			border-top: 1px solid color(grey_03);
			padding-top: v(content-gap-sm) !important;
			padding-bottom: 0 !important;
		}

		:global(.text) {
			@include font(text);
			max-width: none !important;
			display: flex;
			gap: v(content-gap-sm);

			:global(p) {
				@include font(text);
			}
			@include media(phone) {
				flex-direction: column;
				gap: 0;
			}
		}
	}

	:global(.accordion-content-wrapper) {
		background: color(white);
		padding: v(content-gap-sm);
		border-radius: v(border-radius);
		display: flex;
		flex-direction: column;
		position: relative;

		:global(span) {
			@include font(text);
		}
		:global(.accordion-phone) {
			text-decoration: none;
			color: color(black);
			@include font(text);
			&:hover {
				text-decoration: underline;
			}
		}
		:global(.accordion-email) {
			text-decoration: none;
			color: color(black);
			@include font(text);
			&:hover {
				text-decoration: underline;
			}
		}
		:global(.accordion-content) {
			padding: v(content-gap-sm) 0;
			@include media(phone) {
				padding-bottom: 0;
			}
		}
	}
	:global(.accordion-country-title) {
		@include font(h4);
		max-width: 15ch;
		border-bottom: solid 1px color(black);
		padding-bottom: v(content-gap-sm);
		@include media(phone) {
			max-width: 100%;
		}
	}

	:global(.accordion-link-mobile) {
		display: none;
		@include media(phone) {
			display: flex;
			position: absolute;
			top: 55%;
			inset-inline-end: v(content-gap-sm);
		}
	}

	:global(.accordion-link) {
		@include font(h6);
		text-decoration: none;
		display: flex;
		gap: v(content-gap-sm);
		border-top: solid 1px color(black);
		padding-top: v(content-gap-sm);
		color: color(black);
		align-items: center;
		&:hover {
			text-decoration: underline;
		}
		@include media(phone) {
			display: none;
		}
	}
	.country-button {
		@include font(h6);
		border-radius: v(border-radius);
		border: solid 1px color(black);
		padding: 10px 8px;
		transition: all 0.3s ease;
		cursor: pointer;
		white-space: nowrap;

		&:hover {
			background: color(yellow);
			border: transparent 1px solid;
		}

		&.active {
			background: color(yellow);
			border: transparent 1px solid;
		}
	}
</style>
